import { signal } from '@preact/signals-core';
import { activeRoleSignal, getAuthHeaders, deviceModelsSignal } from './assets';
import { showToast } from '../utils/toast';

export const consumablesSignal = signal<any[]>([]);
export const selectedModelIdSignal = signal<string | null>(null);

export async function refreshDeviceModels() {
	try {
		const res = await fetch('/api/v1/device-models', { headers: getAuthHeaders() });
		if (res.ok) {
			deviceModelsSignal.value = await res.json();
			updateDeviceModelsTableRegion();
		}
	} catch (e) {
		console.error('Failed to fetch device models', e);
	}
}

export async function refreshConsumables() {
	try {
		const res = await fetch('/api/v1/consumables', { headers: getAuthHeaders() });
		if (res.ok) {
			consumablesSignal.value = await res.json();
			updateDeviceModelsTableRegion();
		}
	} catch (e) {
		console.error('Failed to fetch consumables', e);
	}
}

export function renderDeviceModelsPage(): string {
	const role = activeRoleSignal.value;
	const isReadOnly = role === 'technician' || role === 'viewer';
	const models = deviceModelsSignal.value || [];
	const consumables = consumablesSignal.value || [];

	const isAdminPlus = role === 'superadmin' || role === 'admin';

	return `
		<s-page heading="Device Models" subtitle="Configure printer catalog pricing and compatibility." inlineSize="large">
			${!isReadOnly ? `
				<div slot="primaryAction" style="display: flex; gap: 0.5rem; align-items: center;">
					<s-button variant="primary" id="btn-open-create-model">Add Model</s-button>
${isAdminPlus ? `
					<s-button variant="secondary" id="btn-open-import-models">Import CSV</s-button>
`: ''}
				</div>
			` : ''}

			<!-- Device Models Table -->
			<s-section>
				<s-box padding="none" background="surface" border="base" borderRadius="base">
					${models.length === 0 ? `
						<div style="padding: 3rem; text-align: center;">
							<s-text tone="subdued">No device models registered in catalog.</s-text>
						</div>
					` : `
						<opm-data-table
							id="device-models-table"
							searchable="true"
							paginated="true"
							page-size="10"
							empty-message="No device models found."
							search-placeholder="Search device models..."
						></opm-data-table>
					`}
				</s-box>
			</s-section>

			<!-- Create Model Modal -->
			<s-modal id="create-model-modal" heading="Create New Device Model">
				<s-form id="form-create-model">
					<s-stack gap="base">
						<s-text-field label="Vendor" name="vendor" placeholder="e.g. HP" required></s-text-field>
						<s-text-field label="Model Name" name="modelName" placeholder="e.g. LaserJet Pro 400" required></s-text-field>
						<s-number-field label="Device Purchase Cost (Rs.)" name="cost" placeholder="0.00" value="0.00" step="0.01" required></s-number-field>
						
						<opm-multi-select
							id="create-model-consumables-select"
							label="Compatible Consumables"
							placeholder="Search consumables..."
							items='${JSON.stringify(consumables.map(c => ({ id: c.id, label: `${c.name} (${c.code})` })))}'
							selected-ids='[]'
						></opm-multi-select>

						<div id="create-model-error" style="display:none; color:var(--p-color-text-critical); margin-top:0.5rem; font-size:0.875rem;"></div>
						<s-stack direction="inline" gap="small" style="margin-top:1.5rem;">
							<s-button variant="primary" id="btn-submit-create-model">Create Model</s-button>
							<s-button commandFor="create-model-modal" command="--hide">Cancel</s-button>
						</s-stack>
					</s-stack>
				</s-form>
			</s-modal>

			<!-- Edit Model Modal -->
			<s-modal id="edit-model-modal" heading="Edit Device Model Details">
				<s-form id="form-edit-model">
					<s-stack gap="base">
						<s-text-field label="Vendor" name="vendor" id="edit-model-vendor-input" required></s-text-field>
						<s-text-field label="Model Name" name="modelName" id="edit-model-name-input" required></s-text-field>
						<s-number-field label="Device Purchase Cost (Rs.)" name="cost" id="edit-model-cost-input" step="0.01" required></s-number-field>
						
						<div id="edit-model-consumables-container">
							<!-- Multi-select populated dynamically -->
						</div>

						<div id="edit-model-error" style="display:none; color:var(--p-color-text-critical); margin-top:0.5rem; font-size:0.875rem;"></div>
						<s-stack direction="inline" gap="small" style="margin-top:1.5rem;">
							<s-button variant="primary" id="btn-submit-edit-model">Save Changes</s-button>
							<s-button commandFor="edit-model-modal" command="--hide">Cancel</s-button>
						</s-stack>
					</s-stack>
				</s-form>
			</s-modal>

			<!-- Delete Model Modal -->
			<s-modal id="delete-model-modal" heading="Delete Device Model?">
				<s-stack gap="base">
					<s-text tone="critical">Are you sure you want to delete this device model? Deleting is permanent and will soft-delete the model.</s-text>
					<div id="delete-model-error" style="display:none; color:var(--p-color-text-critical); margin-top:0.5rem; font-size:0.875rem;"></div>
					<s-stack direction="inline" gap="small" style="margin-top:1.5rem;">
						<s-button variant="primary" tone="critical" id="btn-confirm-delete-model">Delete Model</s-button>
						<s-button commandFor="delete-model-modal" command="--hide">Cancel</s-button>
					</s-stack>
				</s-stack>
			</s-modal>

			<!-- Import Device Models Modal -->
			<s-modal id="import-models-modal" heading="Import Device Models CSV">
				<s-stack gap="base">
					<s-text tone="subdued">
						Upload a CSV file containing printer models. The file must include a header row.
					</s-text>
					<s-box padding="base" background="surface-secondary" border="base" borderRadius="base">
						<s-text style="font-family: monospace;">model_name,vendor,cost<br>LaserJet Pro 400,HP,12000.00<br>imageRUNNER 2206,Canon,25000.00</s-text>
					</s-box>
					<s-stack direction="inline" gap="small" style="align-items: center;">
						<s-button id="btn-select-models-file">Select CSV File</s-button>
						<s-text id="import-models-filename" tone="subdued">No file chosen</s-text>
					</s-stack>
					<input type="file" id="input-models-csv" accept=".csv" style="display: none;" />
					<div id="import-models-error" style="display:none; color:var(--p-color-text-critical); margin-top:0.5rem; font-size:0.875rem;"></div>
					<s-stack direction="inline" gap="small" style="margin-top:1.5rem;">
						<s-button variant="primary" id="btn-submit-import-models">Upload & Import</s-button>
						<s-button commandFor="import-models-modal" command="--hide">Cancel</s-button>
					</s-stack>
				</s-stack>
			</s-modal>
		</s-page>
	`;
}

export function setupDeviceModelsPageListeners() {
	bindDeviceModelsDomEvents();
	updateDeviceModelsTableRegion();
}

function bindDeviceModelsDomEvents() {
	const models = deviceModelsSignal.value || [];
	const consumables = consumablesSignal.value || [];

	// 1. Open Create Modal
	const openCreateBtn = document.getElementById('btn-open-create-model');
	if (openCreateBtn) {
		openCreateBtn.addEventListener('click', () => {
			const modal = document.getElementById('create-model-modal') as any;
			const errDiv = document.getElementById('create-model-error');
			if (errDiv) errDiv.style.display = 'none';
			if (modal && typeof modal.showOverlay === 'function') {
				modal.showOverlay();
			}
		});
	}

	// 2. Submit Create Form
	const submitCreateBtn = document.getElementById('btn-submit-create-model');
	if (submitCreateBtn) {
		submitCreateBtn.addEventListener('click', async (e) => {
			e.preventDefault();
			const form = document.getElementById('form-create-model');
			if (!form) return;

			// Gather selected consumables from multi-select
			const multiSelect = document.getElementById('create-model-consumables-select') as any;
			const checkedConsumableIds = multiSelect ? multiSelect.selectedIds : [];

			const body = {
				vendor: (form.querySelector('[name="vendor"]') as any)?.value || '',
				modelName: (form.querySelector('[name="modelName"]') as any)?.value || '',
				cost: Number((form.querySelector('[name="cost"]') as any)?.value || 0),
				compatibleConsumableIds: checkedConsumableIds,
			};

			const errDiv = document.getElementById('create-model-error');
			if (errDiv) errDiv.style.display = 'none';

			try {
				const res = await fetch('/api/v1/device-models', {
					method: 'POST',
					headers: getAuthHeaders(),
					body: JSON.stringify(body),
				});

				if (res.ok) {
					const modal = document.getElementById('create-model-modal') as any;
					if (modal && typeof modal.hideOverlay === 'function') {
						modal.hideOverlay();
					}
					await refreshDeviceModels();
				} else {
					const data = await res.json();
					if (errDiv) {
						errDiv.textContent = data.error || 'Failed to create model.';
						errDiv.style.display = 'block';
					}
				}
			} catch (err) {
				if (errDiv) {
					errDiv.textContent = 'Network failure.';
					errDiv.style.display = 'block';
				}
			}
		});
	}

	// 3. Configure OpmDataTable and delegated row actions
	const table = document.getElementById('device-models-table') as any;
	if (table) {
		const role = activeRoleSignal.value;
		const isReadOnly = role === 'technician' || role === 'viewer';
		const consumableMap = new Map<string, any>(consumables.map(c => [c.id, c]));

		table.columns = [
			{ key: 'vendor', header: 'Vendor', sortable: true, sortType: 'string' },
			{ key: 'modelName', header: 'Model Name', sortable: true, sortType: 'string' },
			{ key: 'cost', header: 'Cost', sortable: true, sortType: 'number', render: (row: any) => `<strong>Rs. ${row.cost.toFixed(2)}</strong>` },
			{
				key: 'compatibleConsumableIds',
				header: 'Compatible Consumables',
				render: (row: any) => {
					const compNames = (row.compatibleConsumableIds || []).map((id: string) => {
						const c = consumableMap.get(id);
						return c ? `${c.name} (${c.code})` : 'Unknown';
					});
					return compNames.length === 0
						? '<s-badge tone="attention">No Compatible Consumables</s-badge>'
						: `<div style="display:flex; flex-wrap:wrap; gap:0.25rem;">${compNames.map((name: string) => `<s-badge>${name}</s-badge>`).join(' ')}</div>`;
				}
			},
			{
				key: 'actions',
				header: 'Actions',
				render: (row: any) => {
					return !isReadOnly ? `
						<s-button data-action="edit" data-id="${row.id}" size="slim">Edit</s-button>
						<s-button data-action="delete" data-id="${row.id}" tone="critical" size="slim">Delete</s-button>
					` : `<s-text tone="subdued">Read-only</s-text>`;
				}
			}
		];
		table.items = models;

		table.addEventListener('row-action', (e: CustomEvent) => {
			const { action, id } = e.detail;
			selectedModelIdSignal.value = id;

			if (action === 'edit') {
				const m = models.find(x => x.id === id);
				if (!m) return;

				const vendorInput = document.getElementById('edit-model-vendor-input') as HTMLInputElement;
				if (vendorInput) vendorInput.value = m.vendor;

				const nameInput = document.getElementById('edit-model-name-input') as HTMLInputElement;
				if (nameInput) nameInput.value = m.modelName;

				const costInput = document.getElementById('edit-model-cost-input') as HTMLInputElement;
				if (costInput) costInput.value = m.cost.toString();

				const errDiv = document.getElementById('edit-model-error');
				if (errDiv) errDiv.style.display = 'none';

				// Render multi-select dynamically
				const container = document.getElementById('edit-model-consumables-container');
				if (container) {
					container.innerHTML = `
						<opm-multi-select
							id="edit-model-consumables-select"
							label="Compatible Consumables"
							placeholder="Search consumables..."
							items='${JSON.stringify(consumables.map(c => ({ id: c.id, label: `${c.name} (${c.code})` })))}'
							selected-ids='${JSON.stringify(m.compatibleConsumableIds || [])}'
						></opm-multi-select>
					`;
				}

				const modal = document.getElementById('edit-model-modal') as any;
				if (modal && typeof modal.showOverlay === 'function') {
					modal.showOverlay();
				}
			} else if (action === 'delete') {
				const errDiv = document.getElementById('delete-model-error');
				if (errDiv) errDiv.style.display = 'none';

				const modal = document.getElementById('delete-model-modal') as any;
				if (modal && typeof modal.showOverlay === 'function') {
					modal.showOverlay();
				}
			}
		});
	}

	// 4. Submit Edit Form
	const submitEditBtn = document.getElementById('btn-submit-edit-model');
	if (submitEditBtn) {
		submitEditBtn.addEventListener('click', async (e) => {
			e.preventDefault();
			const id = selectedModelIdSignal.value;
			if (!id) return;

			const form = document.getElementById('form-edit-model');
			if (!form) return;

			// Gather selected consumables from multi-select
			const multiSelect = document.getElementById('edit-model-consumables-select') as any;
			const checkedConsumableIds = multiSelect ? multiSelect.selectedIds : [];

			const body = {
				vendor: (form.querySelector('[name="vendor"]') as any)?.value || '',
				modelName: (form.querySelector('[name="modelName"]') as any)?.value || '',
				cost: Number((form.querySelector('[name="cost"]') as any)?.value || 0),
				compatibleConsumableIds: checkedConsumableIds,
			};

			const errDiv = document.getElementById('edit-model-error');
			if (errDiv) errDiv.style.display = 'none';

			try {
				const res = await fetch(`/api/v1/device-models/${id}`, {
					method: 'PUT',
					headers: getAuthHeaders(),
					body: JSON.stringify(body),
				});

				if (res.ok) {
					const modal = document.getElementById('edit-model-modal') as any;
					if (modal && typeof modal.hideOverlay === 'function') {
						modal.hideOverlay();
					}
					await refreshDeviceModels();
				} else {
					const data = await res.json();
					if (errDiv) {
						errDiv.textContent = data.error || 'Failed to update model.';
						errDiv.style.display = 'block';
					}
				}
			} catch (err) {
				if (errDiv) {
					errDiv.textContent = 'Network failure.';
					errDiv.style.display = 'block';
				}
			}
		});
	}

	// 5. Confirm Delete action
	const confirmDeleteBtn = document.getElementById('btn-confirm-delete-model');
	if (confirmDeleteBtn) {
		confirmDeleteBtn.addEventListener('click', async () => {
			const id = selectedModelIdSignal.value;
			if (!id) return;

			const errDiv = document.getElementById('delete-model-error');
			if (errDiv) errDiv.style.display = 'none';

			try {
				const res = await fetch(`/api/v1/device-models/${id}`, {
					method: 'DELETE',
					headers: getAuthHeaders(),
				});

				if (res.ok) {
					const modal = document.getElementById('delete-model-modal') as any;
					if (modal && typeof modal.hideOverlay === 'function') {
						modal.hideOverlay();
					}
					await refreshDeviceModels();
				} else {
					const data = await res.json();
					if (errDiv) {
						errDiv.textContent = data.error || 'Failed to delete model.';
						errDiv.style.display = 'block';
					}
				}
			} catch (err) {
				if (errDiv) {
					errDiv.textContent = 'Network failure.';
					errDiv.style.display = 'block';
				}
			}
		});
	}

	// 6. Import CSV Open Button
	const openImportBtn = document.getElementById('btn-open-import-models');
	if (openImportBtn) {
		openImportBtn.addEventListener('click', () => {
			const modal = document.getElementById('import-models-modal') as any;
			const errDiv = document.getElementById('import-models-error');
			if (errDiv) errDiv.style.display = 'none';
			const filenameText = document.getElementById('import-models-filename');
			if (filenameText) filenameText.textContent = 'No file chosen';
			const fileInput = document.getElementById('input-models-csv') as HTMLInputElement;
			if (fileInput) fileInput.value = '';
			if (modal && typeof modal.showOverlay === 'function') {
				modal.showOverlay();
			}
		});
	}

	// 7. Select CSV File Buttons
	const selectFileBtn = document.getElementById('btn-select-models-file');
	const fileInput = document.getElementById('input-models-csv') as HTMLInputElement;
	if (selectFileBtn && fileInput) {
		selectFileBtn.addEventListener('click', () => {
			fileInput.click();
		});
		fileInput.addEventListener('change', () => {
			const filenameText = document.getElementById('import-models-filename');
			if (filenameText) {
				filenameText.textContent = fileInput.files && fileInput.files[0] ? fileInput.files[0].name : 'No file chosen';
			}
		});
	}

	// 8. Submit CSV Upload Form
	const submitImportBtn = document.getElementById('btn-submit-import-models');
	if (submitImportBtn) {
		submitImportBtn.addEventListener('click', async (e) => {
			e.preventDefault();
			const fileInput = document.getElementById('input-models-csv') as HTMLInputElement;
			const errDiv = document.getElementById('import-models-error');
			if (errDiv) errDiv.style.display = 'none';

			if (!fileInput || !fileInput.files || fileInput.files.length === 0) {
				if (errDiv) {
					errDiv.textContent = 'Please select a CSV file first.';
					errDiv.style.display = 'block';
				}
				return;
			}

			const file = fileInput.files[0];
			if (!file) return;
			const formData = new FormData();
			formData.append('file', file);

			try {
				const res = await fetch('/api/v1/device-models/import', {
					method: 'POST',
					headers: {
						'Authorization': getAuthHeaders()['Authorization'] || '',
					},
					body: formData,
				});

				if (res.ok) {
					const data = await res.json();
					const modal = document.getElementById('import-models-modal') as any;
					if (modal && typeof modal.hideOverlay === 'function') {
						modal.hideOverlay();
					}
					showToast(`Imported ${data.importedCount} and updated ${data.updatedCount} device models successfully!`, 'success');
					await refreshDeviceModels();
				} else {
					const data = await res.json();
					if (errDiv) {
						errDiv.textContent = data.error || 'Failed to import CSV.';
						errDiv.style.display = 'block';
					}
				}
			} catch (err) {
				if (errDiv) {
					errDiv.textContent = 'Network failure.';
					errDiv.style.display = 'block';
				}
			}
		});
	}
}

export function updateDeviceModelsTableRegion() {
	const table = document.getElementById('device-models-table') as any;
	if (table) {
		const models = deviceModelsSignal.value || [];
		const consumables = consumablesSignal.value || [];
		const role = activeRoleSignal.value;
		const isReadOnly = role === 'technician' || role === 'viewer';
		const consumableMap = new Map<string, any>(consumables.map(c => [c.id, c]));

		table.columns = [
			{ key: 'vendor', header: 'Vendor', sortable: true, sortType: 'string' },
			{ key: 'modelName', header: 'Model Name', sortable: true, sortType: 'string' },
			{ key: 'cost', header: 'Cost', sortable: true, sortType: 'number', render: (row: any) => `<strong>Rs. ${row.cost.toFixed(2)}</strong>` },
			{
				key: 'compatibleConsumableIds',
				header: 'Compatible Consumables',
				render: (row: any) => {
					const compNames = (row.compatibleConsumableIds || []).map((id: string) => {
						const c = consumableMap.get(id);
						return c ? `${c.name} (${c.code})` : 'Unknown';
					});
					return compNames.length === 0
						? '<s-badge tone="attention">No Compatible Consumables</s-badge>'
						: `<div style="display:flex; flex-wrap:wrap; gap:0.25rem;">${compNames.map((name: string) => `<s-badge>${name}</s-badge>`).join(' ')}</div>`;
				}
			},
			{
				key: 'actions',
				header: 'Actions',
				render: (row: any) => {
					return !isReadOnly ? `
						<s-button data-action="edit" data-id="${row.id}" size="slim">Edit</s-button>
						<s-button data-action="delete" data-id="${row.id}" tone="critical" size="slim">Delete</s-button>
					` : `<s-text tone="subdued">Read-only</s-text>`;
				}
			}
		];
		table.items = models;
	}
}

export function unmountDeviceModelsPage() {}
